<template>
  <div>
    <cso-component-state
        :componentState="state"
        :customError="error">
      <template v-slot:ready>
        <div v-if="enabled">
          <slot :prozesse="prozesse" />
        </div>
        <div v-if="disabled">
          <div class="digitall-interface-1">
            <div class="container">
              <section>
                <div class="cso-status-page-wrapper">
                  <h2>{{$t('statusPage.headers.disabledPage')}}</h2>
                  <p>{{ errorMessage }}</p>
                  <cso-link :link="backlink || StaticUrls.homepage"
                            :label="$t('general.back')"
                            :is-button="true">
                  </cso-link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:error>
        <div class="digital-inteface-2">
          <div class="container">
            <section>
              <div class="cso-status-page-wrapper">
                <h2>{{$t('statusPage.headers.disabledPage')}}</h2>
                <p>{{ errorMessage }}</p>
                <cso-link :link="backlink || StaticUrls.homepage"
                          :label="$t('general.back')"
                          :is-button="true">
                </cso-link>
              </div>
            </section>
          </div>
        </div>
      </template>
    </cso-component-state>
  </div>
</template>

<script lang="ts">
import di from '@/api/digital-interface'
import * as DI from '@/api/digital-interface/client'
import StaticUrls from '@/common/StaticUrls'
import ComponentsStates from '@/common/ComponentsStates'
import { type I18nType } from '@/common/Model'
import { Subscription } from 'rxjs'
import CsoComponentState from '@/components/core/composition/CsoComponentState.vue'
import store, { type State } from '@/store'

let subscription: Subscription

interface werteObject {
  [key: string]: any | string
}

export default {
  name: 'CsoStatusPageForDigitalInterface',

  components: {
    CsoComponentState
  },

  props: {
    name: {
      type: String,
      required: true
    },
    vk: {
      type: String,
      required: false
    },
    backlink: {
      type: String,
      default: ''
    },
    werte: {
      type: Object as () => werteObject,
      required: false
    },
    prefilledProzesse: {
      type: Object as () => DI.V13.Prozesse,
      required: false
    },
    werteOnlyProzesse: {
      type: Boolean,
      required: false
    },
    useGP: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      state: ComponentsStates.loading,
      StaticUrls,
      prozesse: {} as Readonly<DI.V13.Prozesse>,
      error: '',
      defaultVk: ''
    }
  },

  computed: {
    enabled (): boolean {
      const { VK, GP } = this.prozesse
      return (VK !== undefined || GP !== undefined) && this.prozesse.isEnabled(this.name)
    },
    disabled (): boolean {
      const { VK, GP } = this.prozesse
      return (VK === undefined || GP === undefined) || ((VK || GP) !== undefined && this.prozesse.isDisabled(this.name))
    },
    errorMessage (): I18nType {
      const { VK, GP } = this.prozesse
      const humanDescription = (VK !== undefined || GP !== undefined) && this.prozesse.getFunctionalError(this.name)
      return humanDescription || this.$t('statusPage.messages.disabledPage')
    }
  },

  methods: {
    async getProzesse () {
      try {
        if (this.prefilledProzesse) {
          this.prozesse = this.prefilledProzesse
        } else if (this.werteOnlyProzesse) {
          const prozesse = await di.v13.getProzesse([], [this.name], this.werte)
          this.prozesse = Object.freeze(prozesse)
        } else if (this.useGP) {
          const prozesse = await di.v13.getProzesse([], [this.name], this.werte, di.token!.username!, store.gp)
          this.prozesse = Object.freeze(prozesse)
        } else {
          const prozesse = await di.v13.getProzesse([this.vk || this.defaultVk], [this.name], this.werte, di.token!.username!)
          this.prozesse = Object.freeze(prozesse)
        }
        this.state = ComponentsStates.ready
      } catch (error) {
        if (error instanceof DI.ErrorResponse) {
          this.error = error.text
        }
        this.state = ComponentsStates.reject
      }
    },
    observeStore (state: State) {
      if (state.vk !== this.vk) {
        this.defaultVk = state.vk
      }
    }
  },

  created () {
    subscription = store.subscribe(this.observeStore)
    this.getProzesse()
  },

  unmounted () {
    if (subscription) {
      subscription.unsubscribe()
    }
  }
}
</script>

<style scoped lang="scss">
.cso-status-page-wrapper {
  @include box-shadow(3);
  background-color: $white;
  padding: 20px;
  color: $blue-night;

  @include sm {
    padding: 10px;
    margin-right: -10px;
    margin-left: -10px;
  }

  > a {
    margin-top: 20px;
  }
}
</style>
